// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Card, CardContent, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useContractDocumentsCard } from './use-contract-documents-card';

export const ContractDocumentsCard = defineComponent({
  displayName: 'ContractDocumentsCard',
  useSetup: useContractDocumentsCard,
  View: ({ advisoryTypeKey, isPdfVisible, pdfUrl, togglePdfVisibility }) => {
    return (
      <Card
        variant="outlined"
        className="!rounded-lg"
        data-testid="documents-to-sign-card"
      >
        <CardContent className="!p-7">
          <div className="mb-6 flex items-center gap-2">
            <AppIcon icon="FileText" size="lg" />
            <Typography variant="h4" data-testid="documents-to-sign-title">
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.documents-to-sign.title"
                ns="components"
              />
            </Typography>
          </div>

          <div className="mb-6 flex items-start justify-between gap-4">
            <div className="flex flex-col">
              <Typography
                variant="h5"
                className="!mb-2 text-base font-semibold"
                data-testid="contract-name"
              >
                <Translation
                  i18nKey={`onboarding-process-overview.result-and-signature.documents-to-sign.${advisoryTypeKey}.contract-name`}
                  ns="components"
                />
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                className="text-base leading-6"
                data-testid="contract-subtitle"
              >
                <Translation
                  i18nKey={`onboarding-process-overview.result-and-signature.documents-to-sign.${advisoryTypeKey}.contract-subtitle`}
                  ns="components"
                />
              </Typography>
            </div>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={
                <AppIcon icon={isPdfVisible ? 'EyeOff' : 'Eye'} size="base" />
              }
              data-testid="view-contract-button"
              type="button"
              onClick={togglePdfVisibility}
            >
              <Translation
                i18nKey={`onboarding-process-overview.result-and-signature.documents-to-sign.${advisoryTypeKey}.view-button`}
                ns="components"
              />
            </Button>
          </div>

          {isPdfVisible && (
            <div className="mb-6" data-testid="pdf-viewer-container">
              <iframe
                src={pdfUrl}
                className="w-full rounded-lg border border-gray-300"
                style={{ height: '600px' }}
                data-testid="pdf-viewer-iframe"
                title="PDF Viewer"
              />
            </div>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            className="text-justify text-base leading-6"
            data-testid="signature-note"
          >
            <Translation
              i18nKey={`onboarding-process-overview.result-and-signature.documents-to-sign.${advisoryTypeKey}.signature-note`}
              ns="components"
            />
          </Typography>
        </CardContent>
      </Card>
    );
  },
});
